import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

// Enhanced interfaces for IndexedDB storage
export interface ChatMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  reasoning?: string; // Reasoning content (e.g., from DeepSeek R1)
  images?: ChatMessageImage[]; // Streaming images (e.g., from Gemini image models)
  attachment?: ChatMessageAttachment; // User-attached image or audio file
  model?: string; // Model used for this specific message
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  }; // Token usage for this message (assistant messages only)
  isError?: boolean; // Mark message as error (failed streaming attempt)
  errorCode?: string; // Error code for structured errors
  timestamp: Date;
}

export interface ChatMessageImage {
  url: string; // Base64 data URL
  type: 'image_url';
  index?: number;
}

export interface ChatMessageAudio {
  data: string; // Base64 encoded audio data
  mimeType: string; // "audio/wav" or "audio/mp3"
}

export interface ChatMessageAttachment {
  data: string; // Base64 encoded data
  mimeType: string; // MIME type (e.g., "image/jpeg", "audio/wav")
  type: 'image' | 'audio';
}

export interface Conversation {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  selectedModels: string[]; // History of models used in this conversation
  lastSelectedModel: string; // Most recent model used
  messageCount: number; // Denormalized count for performance
  systemPromptType?: string; // Character ID or 'custom' or null
  systemPromptText?: string; // Custom prompt text if systemPromptType is 'custom'
}

export interface GlobalSettings {
  selectedModel: string;
  selectedCharacter?: string; // Character ID or 'custom'
  customPrompt?: string; // Custom prompt text when character is 'custom'
  chatMemoryLimit?: number; // 0 = unlimited, 1-500 = number of messages, default 20
  lastUpdated: Date;
}

export interface ReasoningSettings {
  reasoningEnabled: boolean;
  maxReasoningTokens: number;
  reasoningEffort?: string; // For OpenAI models
  excludeReasoning: boolean;
}

export interface WebSearchSettings {
  webSearchEnabled: boolean;
  webSearchContextSize?: number;
}

export interface ModelPreferences {
  modelId: string;
  reasoningSettings: ReasoningSettings;
  webSearchSettings?: WebSearchSettings;
  lastUpdated: Date;
}

// IndexedDB Schema
interface ChatDB extends DBSchema {
  conversations: {
    key: string; // conversation.id
    value: Conversation;
    indexes: {
      'by-updated': Date; // updatedAt for sorting
      'by-created': Date; // createdAt for sorting
    };
  };
  messages: {
    key: string; // message.id
    value: ChatMessage;
    indexes: {
      'by-conversation': string; // conversationId for filtering
      'by-timestamp': Date; // timestamp for sorting
    };
  };
  settings: {
    key: string; // setting key (e.g., 'globalModel')
    value: GlobalSettings;
  };
  model_preferences: {
    key: string; // preferences key (e.g., 'preferences:claude-opus-4.1')
    value: ModelPreferences;
    indexes: {
      'by-model': string; // modelId for filtering
      'by-updated': Date; // lastUpdated for sorting
    };
  };
}

class ChatDatabase {
  private db: IDBPDatabase<ChatDB> | null = null;
  private readonly DB_NAME = 'gpt-muse-chat';
  private readonly DB_VERSION = 6; // Incremented for usage and error tracking
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;

    // Ensure we only initialize once
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._init();
    return this.initPromise;
  }

  private async _init(): Promise<void> {
    try {
      // Check if IndexedDB is available
      if (!('indexedDB' in window)) {
        throw new Error('IndexedDB not supported');
      }

      this.db = await openDB<ChatDB>(this.DB_NAME, this.DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          console.log(`Upgrading chat database from ${oldVersion} to ${newVersion}`);

          // Create conversations store
          if (!db.objectStoreNames.contains('conversations')) {
            const conversationStore = db.createObjectStore('conversations', {
              keyPath: 'id',
            });
            conversationStore.createIndex('by-updated', 'updatedAt');
            conversationStore.createIndex('by-created', 'createdAt');
          }

          // Create messages store
          if (!db.objectStoreNames.contains('messages')) {
            const messagesStore = db.createObjectStore('messages', {
              keyPath: 'id',
            });
            messagesStore.createIndex('by-conversation', 'conversationId');
            messagesStore.createIndex('by-timestamp', 'timestamp');
          }

          // Create settings store
          if (!db.objectStoreNames.contains('settings')) {
            db.createObjectStore('settings', {
              keyPath: 'key' as any, // Settings use a string key
            });
          }

          // Create model preferences store
          if (!db.objectStoreNames.contains('model_preferences')) {
            const preferencesStore = db.createObjectStore('model_preferences', {
              keyPath: 'key' as any, // Preferences use a string key like 'preferences:model-id'
            });
            preferencesStore.createIndex('by-model', 'modelId');
            preferencesStore.createIndex('by-updated', 'lastUpdated');
          }
        },
        blocked() {
          console.warn('Database upgrade blocked by another tab');
        },
        blocking() {
          console.warn('Database upgrade is blocking another tab');
        },
      });

      console.log('Chat database initialized successfully');
    } catch (error) {
      console.error('Failed to initialize chat database:', error);
      this.initPromise = null;
      throw new Error(
        `Database initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private async ensureDB(): Promise<IDBPDatabase<ChatDB>> {
    if (!this.db) {
      await this.init();
    }

    if (!this.db) {
      throw new Error('Database not available');
    }

    return this.db;
  }

  // Conversation Operations
  async saveConversation(conversation: Conversation): Promise<void> {
    try {
      const db = await this.ensureDB();

      // Ensure updatedAt is current
      const conversationToSave = {
        ...conversation,
        updatedAt: new Date(),
      };

      console.log('💾 DEBUG: Saving conversation to IndexedDB:', conversationToSave);
      await db.put('conversations', conversationToSave);
    } catch (error) {
      console.error('Failed to save conversation:', error);
      throw new Error(
        `Could not save conversation: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async getConversation(id: string): Promise<Conversation | undefined> {
    try {
      const db = await this.ensureDB();

      const conversation = await db.get('conversations', id);
      if (conversation) {
        // Ensure dates are Date objects (IndexedDB might serialize them)
        return {
          ...conversation,
          createdAt: new Date(conversation.createdAt),
          updatedAt: new Date(conversation.updatedAt),
        };
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get conversation:', error);
      throw new Error(
        `Could not get conversation: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async getAllConversations(): Promise<Conversation[]> {
    try {
      const db = await this.ensureDB();

      const conversations = await db.getAllFromIndex('conversations', 'by-updated');
      console.log('💾 DEBUG: Raw conversations from IndexedDB:', conversations);

      // Convert to proper Date objects and sort by updatedAt descending
      const result = conversations
        .map((conversation) => ({
          ...conversation,
          createdAt: new Date(conversation.createdAt),
          updatedAt: new Date(conversation.updatedAt),
        }))
        .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

      console.log('💾 DEBUG: Processed conversations:', result);
      return result;
    } catch (error) {
      console.error('Failed to get all conversations:', error);
      return []; // Return empty array as fallback
    }
  }

  async deleteConversation(id: string): Promise<void> {
    try {
      const db = await this.ensureDB();

      // Delete conversation and all associated messages in a transaction
      const tx = db.transaction(['conversations', 'messages'], 'readwrite');

      // Delete all messages for this conversation
      const messageKeys = await tx.objectStore('messages').index('by-conversation').getAllKeys(id);
      await Promise.all(messageKeys.map((key) => tx.objectStore('messages').delete(key)));

      // Delete the conversation
      await tx.objectStore('conversations').delete(id);
      await tx.done;
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw new Error(
        `Could not delete conversation: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  // Message Operations
  async addMessage(message: ChatMessage): Promise<void> {
    try {
      const db = await this.ensureDB();

      // Start transaction to add message and update conversation
      const tx = db.transaction(['messages', 'conversations'], 'readwrite');

      // Add the message
      await tx.objectStore('messages').put(message);

      // Update conversation metadata
      const conversation = await tx.objectStore('conversations').get(message.conversationId);
      if (conversation) {
        const updatedConversation = {
          ...conversation,
          messageCount: conversation.messageCount + 1,
          updatedAt: new Date(),
          // Update model history if this message has a model
          selectedModels:
            message.model && !conversation.selectedModels.includes(message.model)
              ? [...conversation.selectedModels, message.model]
              : conversation.selectedModels,
          lastSelectedModel: message.model || conversation.lastSelectedModel,
        };
        await tx.objectStore('conversations').put(updatedConversation);
      }

      await tx.done;
    } catch (error) {
      console.error('Failed to add message:', error);
      throw new Error(
        `Could not add message: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      const db = await this.ensureDB();

      const messages = await db.getAllFromIndex('messages', 'by-conversation', conversationId);

      // Convert dates and sort by timestamp
      return messages
        .map((message) => ({
          ...message,
          timestamp: new Date(message.timestamp),
        }))
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    } catch (error) {
      console.error('Failed to get messages:', error);
      return []; // Return empty array as fallback
    }
  }

  async deleteMessage(messageId: string): Promise<void> {
    try {
      const db = await this.ensureDB();

      // First get the message to know which conversation to update
      const message = await db.get('messages', messageId);
      if (!message) {
        throw new Error(`Message ${messageId} not found`);
      }

      // Start transaction to delete message and update conversation
      const tx = db.transaction(['messages', 'conversations'], 'readwrite');

      // Delete the message
      await tx.objectStore('messages').delete(messageId);

      // Update conversation metadata (decrement message count)
      const conversation = await tx.objectStore('conversations').get(message.conversationId);
      if (conversation) {
        const updatedConversation = {
          ...conversation,
          messageCount: Math.max(0, conversation.messageCount - 1),
          updatedAt: new Date(),
        };
        await tx.objectStore('conversations').put(updatedConversation);
      }

      await tx.done;
    } catch (error) {
      console.error('Failed to delete message:', error);
      throw new Error(
        `Could not delete message: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    try {
      const conversation = await this.getConversation(conversationId);
      if (!conversation) {
        throw new Error(`Conversation ${conversationId} not found`);
      }

      const updatedConversation = {
        ...conversation,
        title,
        updatedAt: new Date(),
      };

      await this.saveConversation(updatedConversation);
    } catch (error) {
      console.error('Failed to update conversation title:', error);
      throw new Error(
        `Could not update conversation title: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  // Settings Operations
  async getGlobalSettings(): Promise<GlobalSettings | undefined> {
    try {
      const db = await this.ensureDB();

      const settings = await db.get('settings', 'globalModel');
      if (settings) {
        return {
          ...settings,
          lastUpdated: new Date(settings.lastUpdated),
        };
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get global settings:', error);
      return undefined; // Return undefined as fallback
    }
  }

  async saveGlobalSettings(settings: Omit<GlobalSettings, 'lastUpdated'>): Promise<void> {
    try {
      const db = await this.ensureDB();

      const settingsToSave = {
        key: 'globalModel',
        selectedModel: settings.selectedModel,
        selectedCharacter: settings.selectedCharacter,
        customPrompt: settings.customPrompt,
        chatMemoryLimit: settings.chatMemoryLimit,
        lastUpdated: new Date(),
      };

      await db.put('settings', settingsToSave as any);
    } catch (error) {
      console.error('Failed to save global settings:', error);
      // Don't throw - this is non-critical
    }
  }

  // Model Preferences Operations
  async getModelPreferences(modelId: string): Promise<ReasoningSettings | undefined> {
    try {
      const db = await this.ensureDB();

      const key = `preferences:${modelId}`;
      const preferences = await db.get('model_preferences', key);

      if (preferences) {
        return preferences.reasoningSettings;
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get model preferences:', error);
      return undefined; // Return undefined as fallback
    }
  }

  async saveModelPreferences(modelId: string, reasoningSettings: ReasoningSettings): Promise<void> {
    try {
      const db = await this.ensureDB();

      const key = `preferences:${modelId}`;

      // Get existing preferences to preserve web search settings
      const existingPreferences = await db.get('model_preferences', key);

      const preferences = {
        key,
        modelId,
        reasoningSettings,
        webSearchSettings: existingPreferences?.webSearchSettings,
        lastUpdated: new Date(),
      };

      await db.put('model_preferences', preferences as any);
    } catch (error) {
      console.error('Failed to save model preferences:', error);
      // Don't throw - this is non-critical
    }
  }

  async deleteModelPreferences(modelId: string): Promise<void> {
    try {
      const db = await this.ensureDB();

      const key = `preferences:${modelId}`;
      await db.delete('model_preferences', key);
    } catch (error) {
      console.error('Failed to delete model preferences:', error);
      // Don't throw - this is non-critical
    }
  }

  // Web Search Preferences Operations
  async getWebSearchPreferences(modelId: string): Promise<WebSearchSettings | undefined> {
    try {
      const db = await this.ensureDB();

      const key = `preferences:${modelId}`;
      const preferences = await db.get('model_preferences', key);

      if (preferences) {
        return preferences.webSearchSettings;
      }
      return undefined;
    } catch (error) {
      console.error('Failed to get web search preferences:', error);
      return undefined; // Return undefined as fallback
    }
  }

  async saveWebSearchPreferences(
    modelId: string,
    webSearchSettings: WebSearchSettings
  ): Promise<void> {
    try {
      const db = await this.ensureDB();

      const key = `preferences:${modelId}`;

      // Get existing preferences to preserve reasoning settings
      const existingPreferences = await db.get('model_preferences', key);

      const preferences = {
        key,
        modelId,
        reasoningSettings: existingPreferences?.reasoningSettings || {
          reasoningEnabled: true,
          maxReasoningTokens: 10000,
          reasoningEffort: 'medium',
          excludeReasoning: false,
        },
        webSearchSettings,
        lastUpdated: new Date(),
      };

      await db.put('model_preferences', preferences as any);
    } catch (error) {
      console.error('Failed to save web search preferences:', error);
      // Don't throw - this is non-critical
    }
  }

  async clearAllData(): Promise<void> {
    try {
      const db = await this.ensureDB();

      const tx = db.transaction(
        ['conversations', 'messages', 'settings', 'model_preferences'],
        'readwrite'
      );
      await Promise.all([
        tx.objectStore('conversations').clear(),
        tx.objectStore('messages').clear(),
        tx.objectStore('settings').clear(),
        tx.objectStore('model_preferences').clear(),
        tx.done,
      ]);
    } catch (error) {
      console.error('Failed to clear all data:', error);
      throw new Error(
        `Could not clear data: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}

// Export singleton instance
export const chatDB = new ChatDatabase();
